import { OperationType } from './../widgets/filters/miscellaneous/operation-type.enum';
import { SelectStatementOptions } from 'src/app/models/select-statement-options.model';
import { BaseSelectStatementId } from 'src/app/enums/base-select-statement-id.enum';
import { SelectionSet, SettingsService } from 'src/app/pages/settings/settings.service';
import { SelectionOptionsPopUpService } from './../selection-options-pop-up.service';
import { FilterUtils, MinifiedArgumentUtils, MinifiedSelectionUtils } from './../models/utils';
import { SelectStatementResult } from 'src/app/models/select-statement-result.model';
import { OverviewFilterInput } from './../pages/stock/stock.service';
import { Injectable, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { BaseService, RequestOptions } from './base.service';
import { QueryFilterParameter } from '../widgets/filters/models/query-filter-parameter.model';
import { FiltersGroupSettings } from '../widgets/filters/models/filters-group-settings.model';

@Injectable({
	providedIn: 'root'
})
export class CoreService {

	private static readonly REQUEST_TOP_PATH: string = "core";

	private _baseService: BaseService = inject(BaseService);

	private readonly _getListElementsByIdRequestEndpoint: string = "GetListElementsById";

	public getListElementsById(listId: number): Observable<string[]> {
		let url = `core/${this._getListElementsByIdRequestEndpoint}?listId=${listId}`;

		return this._baseService.get(url);
	}

	private readonly _getListElementsBySettingNameRequestEndpoint: string = "GetListElementsBySettingName";

	public getListElementsBySettingName(settingName: string): Observable<string[]> {
		let url = `core/${this._getListElementsBySettingNameRequestEndpoint}?settingName=${settingName}`;

		return this._baseService.get(url);
	}

	private readonly _getValueListElementsBySettingNameRequestEndpoint: string = "GetValueListElementsBySettingName";

	public getValueListElementsBySettingName(settingName: string): Observable<string[]> {
		let url = `core/${this._getValueListElementsBySettingNameRequestEndpoint}?settingName=${settingName}`;

		return this._baseService.get(url, { showLoadingIndicator: false });
	}

	private readonly _getSelectStatementDataRequestEndpoint: string = "GetSelectStatementData";

	public async getSelectStatementData<TData = any>(
		options: SelectStatementOptions,
		requestOptions?: RequestOptions,
		addToSelectionOptionsPopUp: boolean = true
	): Promise<Observable<SelectStatementResult<TData>>> {
		const $this = this;

		let url = `core/${this._getSelectStatementDataRequestEndpoint}?baseSelectStatementId=${await options.baseSelectStatementId}&selectionSetId=${await options.selectionSetId ?? ""}&settingsGroupId=${await options.settingsGroupId ?? ""}&topCount=${await options.topCount ?? ""}&periodType=${await options.periodType ?? ""}`;

		const minifiedArguments = await options.minifiedArguments;

		if (minifiedArguments) {
			url += `&${MinifiedArgumentUtils.convertToURLQuery(minifiedArguments)}`;
		}

		const minifiedSelections = await options.minifiedSelections;

		if (minifiedSelections) {
			url += `&${MinifiedSelectionUtils.convertToURLQuery(minifiedSelections)}`;
		}

		const filters = await options.filters;

		if (filters) {
			url += FilterUtils.convertFiltersToURLQuery(filters);
		}

		const selectionSetId = await options.selectionSetId;

		const observable$: Observable<SelectStatementResult<TData>> = this._baseService.get(url, requestOptions);

		return observable$.pipe(
			tap(
				(response: SelectStatementResult<TData>) => {

					if (addToSelectionOptionsPopUp && selectionSetId) {

						$this.getSelectStatementData<SelectionSet>(
							new SelectStatementOptions(
								{
									filters: [
										new QueryFilterParameter(
											'selectionSetId',
											selectionSetId.toString(),
											OperationType.Equals
										)
									],
									baseSelectStatementId: BaseSelectStatementId.DEV_GetSelectionSets,
								}
							),
							(
								requestOptions
									? Object.assign(
										{ ...requestOptions },
										{
											name: `${requestOptions?.name} -> BaseSelectStatementId.GetSelectionSets`
										}
									)
									: requestOptions
							),
							false
						).then(
							(value) => value.subscribe(
								{
									next: (selectStatementResult: SelectStatementResult<SelectionSet>) => {
										const selectionSet: SelectionSet = selectStatementResult.data.find((selectionSet) => selectionSet.selectionSetId === response.selectionSetId);

										if (selectionSet) {
											$this._selectionOptionsPopUpService.addSelectionSet(selectionSet);
										}
									}
								}
							)
						);
					}
				}
			),
		);

		// return observable$;
	}

	private readonly _getFiltersRequestEndpoint: string = "GetFilters";

	public async getFilters(
		options: SelectStatementOptions,
		requestOptions?: Partial<RequestOptions>
	): Promise<Observable<OverviewFilterInput[]>> {
		const url = `core/${this._getFiltersRequestEndpoint}`;

		const requestBody = {
			filters: await options.filters || [],
			baseSelectStatementId: await options.baseSelectStatementId,
			selectionSetId: await options.selectionSetId,
			settingsGroupId: await options.settingsGroupId,
			topCount: await options.topCount
		};

		return this._baseService.post(url, requestBody, requestOptions);
	}

	public getFiltersGroupSettings(filtersOperatorId: number, requestOptions?: RequestOptions): Observable<FiltersGroupSettings> {
		const requestEndpoint: string = "GetFiltersGroupSettings";

		let url = `${CoreService.REQUEST_TOP_PATH}/${requestEndpoint}?filtersOperatorId=${filtersOperatorId}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	public createFiltersGroupSettings(
		filtersOperatorId: number,
		requestOptions?: RequestOptions
	): Observable<FiltersGroupSettings> {
		const requestEndpoint: string = "CreateFiltersGroupSettings";

		let url = `${CoreService.REQUEST_TOP_PATH}/${requestEndpoint}?filtersOperatorId=${filtersOperatorId}`;

		return this._baseService.post(
			url,
			undefined,
			requestOptions
		);
	}

	public updateFiltersGroupSettings(
		filtersGroupSettings: FiltersGroupSettings,
		requestOptions?: RequestOptions
	): Observable<null> {
		const requestEndpoint: string = "UpdateFiltersGroupSettings";

		let url = `${CoreService.REQUEST_TOP_PATH}/${requestEndpoint}?filtersOperatorId=${filtersGroupSettings.filtersOperatorId}&numberOfColumns=${filtersGroupSettings.numberOfColumns}`;

		return this._baseService.put(
			url,
			undefined,
			requestOptions
		);
	}

	constructor(
		private _settingsService: SettingsService,
		private _selectionOptionsPopUpService: SelectionOptionsPopUpService,
	) { }
}
